import { useState, useEffect } from 'react';
import {
  getMe,
  login as apiLogin,
  logout as apiLogout,
  onSessionExpired,
  resetSessionExpiredNotification,
} from '../api/client';
import type { LoginResponse } from '../api/client';

interface AuthState {
  user: LoginResponse['user'] | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
}

let authState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  sessionExpired: false,
};

let initialLoadPromise: Promise<void> | null = null;
let authStateVersion = 0;
const listeners = new Set<() => void>();

function setAuthState(nextState: AuthState) {
  authState = nextState;
  authStateVersion += 1;
  for (const listener of listeners) {
    listener();
  }
}

function loadInitialAuthState() {
  const loadVersion = authStateVersion;

  initialLoadPromise ??= getMe()
    .then((res) => {
      if (authStateVersion === loadVersion) {
        setAuthState({ user: res.user, isAuthenticated: true, isLoading: false, sessionExpired: false });
      }
    })
    .catch(() => {
      if (authStateVersion === loadVersion) {
        setAuthState({ user: null, isAuthenticated: false, isLoading: false, sessionExpired: false });
      }
    });

  return initialLoadPromise;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(authState);

  useEffect(() => {
    const listener = () => setState(authState);
    listeners.add(listener);
    const unsubscribeSessionExpired = onSessionExpired(() => {
      initialLoadPromise = null;
      setAuthState({ user: null, isAuthenticated: false, isLoading: false, sessionExpired: true });
    });
    if (!authState.sessionExpired) {
      void loadInitialAuthState();
    }

    return () => {
      listeners.delete(listener);
      unsubscribeSessionExpired();
    };
  }, []);

  const login = async (username: string, password: string) => {
    const res = await apiLogin(username, password);
    initialLoadPromise = null;
    resetSessionExpiredNotification();
    setAuthState({ user: res.user, isAuthenticated: true, isLoading: false, sessionExpired: false });
  };

  const logout = async () => {
    await apiLogout().catch(() => undefined);
    initialLoadPromise = null;
    resetSessionExpiredNotification();
    setAuthState({ user: null, isAuthenticated: false, isLoading: false, sessionExpired: false });
  };

  return { ...state, login, logout };
}
